import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Activity, TrendingUp, Users, FolderKanban, CheckCircle2, AlertTriangle, Clock, Target, UserCheck, Briefcase } from 'lucide-react';
import { apiFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#155179';

const CircularProgress = ({ value, size = 120, strokeWidth = 10, color = BRAND, label, sublabel, delay = 0 }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (animatedValue / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const step = () => {
        start += 2;
        if (start > value) start = value;
        setAnimatedValue(start);
        if (start < value) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e2e8f0" strokeWidth={strokeWidth} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={strokeWidth} fill="none"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition-all duration-700 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-900">{animatedValue}%</span>
        </div>
      </div>
      <span className="mt-3 text-sm font-semibold text-slate-700">{label}</span>
      {sublabel && <span className="text-xs text-slate-500 mt-0.5">{sublabel}</span>}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sublabel, iconColor }) => {
  const c = iconColor || BRAND;
  return (
    <Card className="p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '20', color: c }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {sublabel && <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>}
      </div>
    </Card>
  );
};

const Reports = () => {
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
  const userId = user?.id;

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, teamRes] = await Promise.all([
        fetch(`${API}/api/performance-stats`, { headers: { 'X-User-Id': userId } }),
        apiFetch('/api/team'),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (teamRes.ok) setTeam(await teamRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 rounded-full" style={{ borderTopColor: BRAND }} />
      </div>
    );
  }

  if (!stats || stats.totalProjets === 0) {
    return (
      <div className="p-6" data-testid="performance-page">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Performance Projets</h1>
        <Card className="p-12 text-center">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucune donnée disponible</h3>
          <p className="text-sm text-slate-500">Créez des projets pour voir les métriques de performance.</p>
        </Card>
      </div>
    );
  }

  // Team stats
  const totalTeam = team.length;
  const avgWorkload = totalTeam > 0 ? Math.round(team.reduce((s, m) => s + (m.current_workload || 0), 0) / totalTeam) : 0;
  const availableCount = team.filter(m => m.availability === 'Disponible').length;
  const overloadedCount = team.filter(m => (m.current_workload || 0) >= 80).length;
  const topMembers = [...team].sort((a, b) => (b.current_workload || 0) - (a.current_workload || 0)).slice(0, 5);

  const circularMetrics = [
    { value: stats.progressionMoyenne, color: BRAND, label: 'Progression moyenne', sublabel: `${stats.projetsEnCours} projets en cours`, delay: 0 },
    { value: stats.tauxCompletion, color: '#0891b2', label: 'Tâches complétées', sublabel: `${stats.actionsFaites} / ${stats.totalActions} actions`, delay: 150 },
    { value: stats.santeProjets, color: stats.santeProjets >= 70 ? '#059669' : stats.santeProjets >= 40 ? '#d97706' : '#dc2626', label: 'Santé des projets', sublabel: `${stats.projetsEnRetard} à risque`, delay: 300 },
    { value: stats.chargeEquipe, color: stats.chargeEquipe >= 85 ? '#dc2626' : stats.chargeEquipe >= 60 ? '#d97706' : BRAND, label: 'Charge équipe', sublabel: 'Taux d\'occupation', delay: 450 },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Performance Projets</h1>
        <Badge variant="outline" className="text-sm">{stats.totalProjets} projets</Badge>
      </div>

      {/* Circular Metrics */}
      <Card className="p-6 sm:p-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center">
          {circularMetrics.map((m) => (
            <CircularProgress key={m.label} value={m.value} color={m.color} label={m.label} sublabel={m.sublabel} delay={m.delay} />
          ))}
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderKanban} label="Total projets" value={stats.totalProjets} iconColor={BRAND} />
        <StatCard icon={CheckCircle2} label="Projets livrés" value={stats.projetsTermines} sublabel={`${stats.tauxLivraison}% taux de livraison`} iconColor="#059669" />
        <StatCard icon={Clock} label="Projets en cours" value={stats.projetsEnCours} iconColor="#0891b2" />
        <StatCard icon={AlertTriangle} label="Projets à risque" value={stats.projetsEnRetard} iconColor="#dc2626" />
      </div>

      {/* Actions summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard icon={Target} label="Actions totales" value={stats.totalActions} iconColor="#7c3aed" />
        <StatCard icon={CheckCircle2} label="Actions terminées" value={stats.actionsFaites} iconColor="#059669" />
        <StatCard icon={TrendingUp} label="Actions en cours" value={stats.actionsEnCours} iconColor="#d97706" />
      </div>

      {/* Team Stats Section */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" style={{ color: BRAND }} /> Statistiques Équipe
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Total membres" value={totalTeam} iconColor={BRAND} />
          <StatCard icon={Briefcase} label="Charge moyenne" value={`${avgWorkload}%`} sublabel="Taux d'occupation moyen" iconColor="#7c3aed" />
          <StatCard icon={UserCheck} label="Disponibles" value={availableCount} sublabel={`sur ${totalTeam} membres`} iconColor="#059669" />
          <StatCard icon={AlertTriangle} label="En surcharge" value={overloadedCount} sublabel="80% ou plus" iconColor="#dc2626" />
        </div>

        {/* Top members workload */}
        {topMembers.length > 0 && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Charge de travail par membre</h3>
            <div className="space-y-4">
              {topMembers.map((m) => {
                const wl = m.current_workload || 0;
                const color = wl >= 80 ? '#dc2626' : wl >= 50 ? '#d97706' : BRAND;
                return (
                  <div key={m.id} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: BRAND }}>
                      {m.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700 truncate">{m.name}</span>
                        <span className="font-semibold ml-2" style={{ color }}>{wl}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${wl}%`, backgroundColor: color }} />
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">{m.role}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Reports;
